import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { ResetIcon } from "@/assets/icons";
import { Switch } from "@/components/ui/switch";
import { COLORS } from "@/constants/colors";
import {
  DEFAULT_STEP,
  MAX_STEP,
  MIN_STEP,
  OTHER_ROW_ID
} from "@/constants/dataset";
import { RootState } from "@/store";
import {
  CategoryRow,
  CustomOrdinalValue,
  DatasetAttributeSchema,
  DraftState,
  EColorNames,
  OrdinalState,
  RowMarkerFields
} from "@/types/dataset";
import { getColorScaleMap } from "@/utils/color";
import {
  getAttributeTableValues,
  getDefaultCustomOrdinalConfig,
  hslToRgb,
  rgbToHex
} from "@/utils/dataset";

import EditCategoryPanel from "./EditCategoryPanel";
import {
  LegendActions,
  LegendColumnHeaders,
  LegendOtherRow,
  LegendRow,
  LegendRowActivePanel
} from "./LegendRow";
import { useLegendSubPanels } from "./useLegendSubPanels";

interface CustomOrdinalConfigProps {
  isPOI?: boolean;
  /** Line datasets: single "Stroke" color column (no Fill/outline), drives line-color. */
  strokeOnly?: boolean;
  disabled?: boolean;
  availableValues?: string[];
  datasetId?: string;
  selectedCategory?: DatasetAttributeSchema;
  colorSteps?: number;
  colorFamily?: EColorNames;
  value?: CustomOrdinalValue;
  onPreview?: (value: CustomOrdinalValue) => void;
  onSave?: (value: CustomOrdinalValue) => void;
  onCancel?: () => void;
  onReset?: () => CustomOrdinalValue | null;
}

const DEFAULT_OUTLINE_COLOR = rgbToHex(hslToRgb({ h: 183, s: 60, l: 28 }));

const paletteColor = (
  colorFamily: EColorNames,
  index: number,
  total: number
): string => {
  const scale = getColorScaleMap(colorFamily, Math.max(total, 1)) ?? [];
  const key = scale[index] ?? scale[scale.length - 1];
  return COLORS[colorFamily]?.[key] ?? DEFAULT_OUTLINE_COLOR;
};

const mapValueToState = (value: CustomOrdinalValue): OrdinalState => ({
  rows: value.steps.map((step, index) => ({
    id: `cat-${index}`,
    name: step.label,
    values: step.values,
    fillColor: step.fillColor,
    outlineColor: step.outlineColor,
    image: step.imageUrl,
    imageName: step.imageName,
    imageAssetId: step.imageAssetId,
    iconId: step.iconId ?? null
  })),
  otherFill: value.otherFillColor,
  otherOutline: value.otherOutlineColor
});

const mapStateToValue = (state: OrdinalState): CustomOrdinalValue => ({
  steps: state.rows.map((row) => ({
    label: row.name,
    values: row.values,
    fillColor: row.fillColor,
    outlineColor: row.outlineColor,
    imageUrl: row.image,
    imageName: row.imageName,
    imageAssetId: row.imageAssetId,
    iconId: row.iconId
  })),
  otherFillColor: state.otherFill,
  otherOutlineColor: state.otherOutline
});

const CustomOrdinalConfig = ({
  isPOI = false,
  strokeOnly = false,
  disabled = false,
  availableValues,
  datasetId,
  selectedCategory,
  colorSteps = DEFAULT_STEP,
  colorFamily = EColorNames.TEAL,
  value,
  onPreview,
  onSave,
  onCancel,
  onReset
}: CustomOrdinalConfigProps) => {
  const [useImage, setUseImage] = useState<boolean>(() =>
    Boolean(value?.steps?.some((step) => step.imageUrl || step.iconId))
  );
  const lastMarkersRef = useRef<Map<string, RowMarkerFields> | null>(null);
  const [draftState, setDraftState] = useState<DraftState | null>(null);

  const rawDataset = useSelector((state: RootState) =>
    datasetId ? state.dataset.rawDatasets?.[datasetId] : undefined
  );
  const tableValues = useMemo(
    () => getAttributeTableValues(rawDataset, selectedCategory?.attribute_name),
    [rawDataset, selectedCategory]
  );

  const allValues = useMemo(() => {
    if (tableValues.length) return tableValues;
    if (availableValues?.length) return availableValues;
    return [];
  }, [tableValues, availableValues]);

  const isLoadingValues = false;

  const base = useMemo((): OrdinalState => {
    if (!value)
      return mapValueToState(
        getDefaultCustomOrdinalConfig(colorFamily, colorSteps, tableValues)
      );
    const hydrated = mapValueToState(value);
    if (!tableValues.length) return hydrated;
    const available = new Set(tableValues);
    const rows = hydrated.rows.map((row) => {
      const kept = row.values.filter((v) => available.has(v));
      return kept.length === row.values.length ? row : { ...row, values: kept };
    });
    return { ...hydrated, rows };
  }, [value, colorFamily, colorSteps, tableValues]);

  // Unsaved edits stay live only while they branch from the CURRENT `base`;
  // any external input change (palette, step count, data) rebuilds `base`,
  // instantly discarding the now-stale draft.
  const currentDraft =
    draftState !== null && draftState.base === base ? draftState : null;

  const draft = currentDraft?.state ?? base;
  const isDirty = draft !== base; // reference equality is sufficient
  const nextId = currentDraft?.nextId ?? base.rows.length;

  const {
    activeColor,
    setActiveColor,
    editingRowId,
    setEditingRowId,
    openColor,
    openEdit,
    isActiveSwatch,
    activeRow,
    activeColorValue
  } = useLegendSubPanels({
    disabled,
    rows: draft.rows,
    otherFill: draft.otherFill,
    otherOutline: draft.otherOutline
  });

  const setDraft = useCallback(
    (
      updater: OrdinalState | ((prev: OrdinalState) => OrdinalState),
      nextIdOverride?: number
    ) => {
      const next = typeof updater === "function" ? updater(draft) : updater;
      setDraftState({
        base,
        state: next,
        nextId: nextIdOverride ?? nextId
      });
      onPreview?.(mapStateToValue(next));
    },
    [draft, base, nextId, onPreview]
  );

  const imageMode = useImage && isPOI;
  const maxSteps = Math.min(Math.max(colorSteps, MIN_STEP), MAX_STEP);

  const updateRow = (id: string, patch: Partial<CategoryRow>) =>
    setDraft((prev) => ({
      ...prev,
      rows: prev.rows.map((r) => (r.id === id ? { ...r, ...patch } : r))
    }));

  // Adding steps
  const handleAddStep = () => {
    // Add Step is capped at the active color scale step number (`maxSteps`).
    if (draft.rows.length >= maxSteps) return;
    const color = paletteColor(
      colorFamily,
      draft.rows.length,
      draft.rows.length + 1
    );
    setDraft(
      (prev) => ({
        ...prev,
        rows: [
          ...prev.rows,
          {
            id: `cat-${nextId}`,
            name: `Category ${prev.rows.length + 1}`,
            values: [],
            fillColor: color,
            outlineColor: color,
            image: null,
            imageName: null,
            imageAssetId: null,
            iconId: null
          }
        ]
      }),
      nextId + 1
    );
  };

  // Removing steps
  const handleRemoveStep = (id: string) => {
    if (draft.rows.length <= 1) return;
    // Clear the current color selection since removing a row changes the list.
    setActiveColor(null);
    setEditingRowId(null);
    setDraft((prev) => ({
      ...prev,
      rows: prev.rows.filter((r) => r.id !== id)
    }));
  };

  // Resetting config to default
  const handleReset = () => {
    const resetConfig = onReset?.();
    if (resetConfig) {
      setDraftState(null);
    } else {
      const next = mapValueToState(
        getDefaultCustomOrdinalConfig(
          colorFamily,
          colorSteps ?? draft.rows.length,
          tableValues
        )
      );
      setDraftState({ base, state: next, nextId: next.rows.length });
      onSave?.(mapStateToValue(next));
    }
    setActiveColor(null);
    setEditingRowId(null);
    setUseImage(false);
    lastMarkersRef.current = null;
  };

  // Change to image or icon mode
  const handleToggleImage = (checked: boolean) => {
    setUseImage(checked);
    setEditingRowId(null);
    if (checked) {
      // Enabling Image while a category row's color picker is open swaps it for
      // that row's Marker Visual panel (matches Simple). The "Other" row has no
      // per-row image, so its picker is left as-is.
      setActiveColor((prev) =>
        prev && prev.rowId !== OTHER_ROW_ID
          ? { ...prev, target: "image" }
          : prev
      );
      const lastMarkers = lastMarkersRef.current;
      if (!lastMarkers) return;
      setDraft((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => {
          const marker = lastMarkers.get(r.id);
          return marker ? { ...r, ...marker } : r;
        })
      }));
    } else {
      const markers = new Map<string, RowMarkerFields>(
        draft.rows
          .filter((r) => r.image || r.iconId)
          .map((r) => [
            r.id,
            {
              image: r.image,
              imageName: r.imageName,
              imageAssetId: r.imageAssetId,
              iconId: r.iconId
            }
          ])
      );
      if (markers.size) lastMarkersRef.current = markers;
      setActiveColor(null);
      setDraft((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => ({
          ...r,
          image: null,
          imageName: null,
          imageAssetId: null,
          iconId: null
        }))
      }));
    }
  };

  // Saving config
  const handleSave = () => {
    onSave?.(mapStateToValue(draft));
    setDraftState(null);
    setActiveColor(null);
    setEditingRowId(null);
  };

  // Canceling edits config
  const handleCancel = () => {
    setDraftState(null);
    onCancel?.();
    setActiveColor(null);
    setEditingRowId(null);
  };

  const editingRow = draft.rows.find((r) => r.id === editingRowId) ?? null;
  const valuesAssignedElsewhere = editingRow
    ? draft.rows.filter((r) => r.id !== editingRow.id).flatMap((r) => r.values)
    : [];

  const handleActiveColorChange = (hex: string) => {
    if (!activeColor?.rowId) return;
    if (activeColor.rowId === OTHER_ROW_ID) {
      setDraft((prev) => ({
        ...prev,
        ...(activeColor.target === "outline"
          ? { otherOutline: hex }
          : { otherFill: hex })
      }));
      return;
    }
    updateRow(
      activeColor.rowId,
      activeColor.target === "outline"
        ? { outlineColor: hex }
        : { fillColor: hex }
    );
  };

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-[#e5e7eb] p-3">
      {/* Image toggle + Reset */}
      <div className="flex items-center justify-between">
        {isPOI ? (
          <button
            type="button"
            className="contents"
            // Don't let the toggle's press dismiss the color picker, so Image
            // swaps the open picker to a marker instead of closing it.
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <label className="flex items-center gap-2 text-xs text-txtHeading">
              <Switch
                checked={useImage}
                onCheckedChange={handleToggleImage}
                disabled={disabled}
                data-testid="ordinal-image-toggle"
              />
              Image
            </label>
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={handleReset}
          // Always available (for every dataset) — Reset returns to the active
          // color scale default regardless of whether there are pending edits.
          disabled={disabled}
          className="text-xs text-primary-teal disabled:opacity-50"
          data-testid="ordinal-reset"
        >
          <div className="flex flex-row items-center">
            <ResetIcon className="mr-1 inline h-3 w-3" />
            Reset
          </div>
        </button>
      </div>

      <LegendColumnHeaders
        imageMode={imageMode}
        strokeOnly={strokeOnly}
        withActions
      />

      {/* Category rows */}
      <div className="flex flex-col gap-2">
        {draft.rows.map((row) => (
          <LegendRow
            key={row.id}
            imageMode={imageMode}
            strokeOnly={strokeOnly}
            image={row.image}
            iconId={row.iconId}
            fillColor={row.fillColor}
            outlineColor={row.outlineColor}
            isFillActive={isActiveSwatch(row.id, ["fill", "image"])}
            isOutlineActive={isActiveSwatch(row.id, ["outline"])}
            disabled={disabled}
            onFillClick={() => openColor(row.id, imageMode ? "image" : "fill")}
            onOutlineClick={() => openColor(row.id, "outline")}
            fillTestId={`ordinal-fill-${row.id}`}
            outlineTestId={`ordinal-outline-${row.id}`}
            onEdit={() => openEdit(row.id)}
            editAriaLabel="Edit category"
            editTestId={`ordinal-edit-${row.id}`}
            onDelete={() => handleRemoveStep(row.id)}
            deleteAriaLabel="Delete category"
            deleteTestId={`ordinal-delete-${row.id}`}
            deleteDisabled={draft.rows.length <= 1}
          >
            <span className="truncate text-xs font-medium text-txtHeading">
              {row.name}
            </span>
            <span
              className="truncate text-[10px] text-grey"
              data-testid={`ordinal-values-count-${row.id}`}
            >
              {row.values.length ? `${row.values.length} Selected` : "No Value"}
            </span>
          </LegendRow>
        ))}
      </div>

      {/* Add Step */}
      <button
        type="button"
        onClick={handleAddStep}
        disabled={disabled || draft.rows.length >= maxSteps}
        className="flex items-center justify-center gap-1 py-1 text-xs font-medium text-primary-teal disabled:opacity-50"
        data-testid="ordinal-add-step"
      >
        + Add Step
      </button>

      <div className="h-px bg-gainsboro" />

      {/* Other row (system-managed) */}
      <LegendOtherRow
        testIdPrefix="ordinal"
        strokeOnly={strokeOnly}
        fillColor={draft.otherFill}
        outlineColor={draft.otherOutline}
        isFillActive={isActiveSwatch(OTHER_ROW_ID, ["fill"])}
        isOutlineActive={isActiveSwatch(OTHER_ROW_ID, ["outline"])}
        disabled={disabled}
        onFillClick={() => openColor(OTHER_ROW_ID, "fill")}
        onOutlineClick={() => openColor(OTHER_ROW_ID, "outline")}
      />

      {/* Cancel / Save */}
      <LegendActions
        disabled={disabled || !isDirty}
        onCancel={handleCancel}
        onSave={handleSave}
      />

      <LegendRowActivePanel
        activeColor={activeColor}
        activeRow={activeRow}
        activeColorValue={activeColorValue}
        updateRow={updateRow}
        onColorChange={handleActiveColorChange}
        onClose={() => setActiveColor(null)}
      />

      {editingRow && (
        <EditCategoryPanel
          // Remount per row so the draft name/values re-init to the clicked
          // category — clicking another row's Edit without closing the panel
          // must show that row's name, not the previously opened one.
          key={editingRow.id}
          categoryName={editingRow.name}
          assignedValues={editingRow.values}
          attributeValues={allValues}
          valuesInOtherCategories={valuesAssignedElsewhere}
          isLoadingValues={isLoadingValues}
          onSave={(name, values) => updateRow(editingRow.id, { name, values })}
          onClose={() => setEditingRowId(null)}
        />
      )}
    </div>
  );
};

export default CustomOrdinalConfig;
